"use client";

import { useEffect, useRef, type CSSProperties, type ElementType, type ReactNode } from "react";

/**
 * Révélation déclenchée par le DÉFILEMENT.
 *
 * À réserver aux pages longues (marketing), où `<Reveal>` — qui s'anime au
 * montage — ferait tout apparaître d'un coup, y compris le contenu situé très
 * en dessous de la ligne de flottaison. Sur le dashboard, `<Reveal>` reste le
 * défaut : les écrans y tiennent en une hauteur ou deux.
 *
 * Remplace `useReveal`/`RevealSection`, qui avaient deux défauts :
 *
 * 1. **Un flash.** L'état initial rendu par le serveur était `reveal-init`
 *    (opacité 1), remplacé après montage par `reveal` (opacité 0), puis par
 *    `reveal visible`. Le contenu s'affichait, disparaissait, puis
 *    réapparaissait — visible à chaque chargement sur tout ce qui est en haut
 *    de page. Ici l'état caché est posé par la CSS dès le premier paint, via
 *    la classe `.rl-js` que le script bloquant écrit sur <html>.
 * 2. **Un div parasite.** Le wrapper n'était pas stylable ; dans le hero il
 *    devenait un flex-item qui rétrécissait, cassant la largeur du séparateur
 *    en dessous. `as` et `className` permettent maintenant de le contrôler.
 *
 * L'observateur se déconnecte après le premier déclenchement : rien ne reste
 * actif une fois la section vue.
 */
export function RevealOnScroll({
  i,
  as: Tag = "div",
  className = "",
  style,
  children,
}: {
  /** Rang dans la cascade — même sémantique que `<Reveal i>`. */
  i?: number;
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Déjà dans le viewport au montage (haut de page) : révéler tout de suite,
    // sans attendre un événement de défilement qui pourrait ne jamais venir.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("rl-in");
        observer.disconnect();
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`rl-scroll ${className}`.trim()}
      style={i === undefined ? style : ({ ...style, ["--i"]: i } as CSSProperties)}
    >
      {children}
    </Tag>
  );
}
